import { ELEMENTS, type Element, type Locale } from './constants'
import {
  branchesClash,
  branchesCombine,
  branchesTrine,
  controls,
  generates,
  stemElement,
  stemsClash,
  stemsCombine,
} from './ganzhi'

/**
 * 궁합 점수 — 순수 함수.
 *
 * 입력은 두 사람의 **일주(일간·일지)와 오행 개수**뿐이다. 이름·관계명은 받지도 않는다.
 * 규칙은 널리 쓰이는 기본 원리만 쓴다:
 *  - 일간끼리: 천간합(+), 천간충(−), 같은 오행(+), 상생(+), 상극(−)
 *  - 일지끼리: 육합(+), 삼합 같은 무리(+), 충(−)
 *  - 오행 보완: 한쪽에 없는 오행을 다른 쪽이 넉넉히 가졌으면(+)
 * 점수는 20~99 로 자른다. 100점과 0점은 단정처럼 보여서 쓰지 않는다 (말투 규칙: 단정 금지).
 */

export interface CompatSide {
  dayPillar: string
  elements: { element: Element; count: number }[]
}

export type CompatFactor =
  | 'stemCombine'
  | 'stemClash'
  | 'sameElement'
  | 'generates'
  | 'controls'
  | 'branchCombine'
  | 'branchTrine'
  | 'branchClash'
  | 'complement'

export type CompatBand = 'excellent' | 'good' | 'balanced' | 'effort' | 'challenging'

export interface CompatResult {
  score: number
  band: CompatBand
  /** 점수에 영향을 준 요소. 영향이 큰 순서. */
  factors: { factor: CompatFactor; points: number }[]
}

const BASE = 60
const MIN = 20
const MAX = 99

export function compatibility(a: CompatSide, b: CompatSide): CompatResult {
  const [aStem, aBranch] = [...a.dayPillar]
  const [bStem, bBranch] = [...b.dayPillar]
  if (!aStem || !aBranch || !bStem || !bBranch) throw new Error('일주가 두 글자가 아니다')

  const factors: { factor: CompatFactor; points: number }[] = []
  const add = (factor: CompatFactor, points: number) => factors.push({ factor, points })

  // --- 일간 ---
  const ae = stemElement(aStem)
  const be = stemElement(bStem)
  if (stemsCombine(aStem, bStem)) add('stemCombine', 15)
  else if (stemsClash(aStem, bStem)) add('stemClash', -10)
  else if (ae === be) add('sameElement', 5)
  else if (generates(ae, be) || generates(be, ae)) add('generates', 8)
  else if (controls(ae, be) || controls(be, ae)) add('controls', -4)

  // --- 일지 ---
  if (branchesCombine(aBranch, bBranch)) add('branchCombine', 12)
  else if (branchesClash(aBranch, bBranch)) add('branchClash', -12)
  else if (branchesTrine(aBranch, bBranch)) add('branchTrine', 8)

  // --- 오행 보완 ---
  const count = (side: CompatSide, e: Element) => side.elements.find((x) => x.element === e)?.count ?? 0
  let complement = 0
  for (const e of ELEMENTS) {
    if (count(a, e) === 0 && count(b, e) >= 2) complement += 3
    if (count(b, e) === 0 && count(a, e) >= 2) complement += 3
  }
  complement = Math.min(complement, 9)
  if (complement > 0) add('complement', complement)

  const raw = BASE + factors.reduce((n, f) => n + f.points, 0)
  const score = Math.max(MIN, Math.min(MAX, raw))
  factors.sort((x, y) => Math.abs(y.points) - Math.abs(x.points))
  return { score, band: bandOf(score), factors }
}

export function bandOf(score: number): CompatBand {
  if (score >= 85) return 'excellent'
  if (score >= 72) return 'good'
  if (score >= 58) return 'balanced'
  if (score >= 45) return 'effort'
  return 'challenging'
}

/**
 * 유료 상세 풀이의 열쇠에 드는 한 사람 몫. 프롬프트는 이 값(일주, 강·약 오행, 성별, 언어)만 쓴다. 개인 정보는 없다.
 */
export interface CompatKeySide {
  dayPillar: string
  strongest: Element
  weakest: Element
  /** 그 사람의 성별 (입력값, 모르면 없음) */
  sex?: 'female' | 'male' | null
  /** 같은 수로 가장 많은·적은 기운 모두 (동점일 때만) */
  most?: Element[]
  least?: Element[]
}

const sideOf = (s: CompatKeySide) => `${s.dayPillar}.${s.strongest}.${s.weakest}`
const sexOf = (s?: 'female' | 'male' | null) => (s === 'female' ? 'f' : s === 'male' ? 'm' : 'u')

/** 동점이 있으면 그 목록도 열쇠에 (재료가 달라진다) */
const tieOf = (s: CompatKeySide) =>
  (s.most && s.most.length > 1) || (s.least && s.least.length > 1) ? `.t${(s.most ?? [s.strongest]).join('+')}~${(s.least ?? [s.weakest]).join('+')}` : ''

/** 한 사람 몫의 열쇠 조각 — 일주·강약·성별·동점 */
export const compatSideKey = (s: CompatKeySide) => `${sideOf(s)}.${sexOf(s.sex)}${tieOf(s)}`

/**
 * 좋은/조심할 때를 쓰는 뒤 조각 (연인·배우자 love2 · 짝사랑 crush2 · 헤어진 사람 ex2) — 두 사람이 함께 좋은 달·엇갈리는 달을
 * 재료로 받고, 기준 달(YYYY-MM)이 캐시 열쇠에 든다. 열쇠·프롬프트·공유 글 찾기가 이 한 함수를 본다.
 */
export function isMonthPart(part: string): boolean {
  return part === 'love2' || part === 'crush2' || part === 'ex2'
}

/** 재료를 바꾸면 올린다 — 모든 언어가 같은 판이다 */
export const COMPAT_VERSION = 'v22'

/**
 * **궁합 캐시 열쇠 — 하나다.** 무슨 사이인지·어느 조각인지·앞사람(당신) → 상대 순서·성별까지 넣는다
 * (순서를 바꾸면 "당신" 과 상대가 뒤바뀐 글이 나간다). 시기 조각은 기준 달과 사주 주인의 달 목록(`month`)이 붙는다.
 */
export function compatCacheKey(a: CompatKeySide, b: CompatKeySide, locale: Locale, kind: string, part: string, month?: string, youth = false): string {
  return ['compat', COMPAT_VERSION, compatSideKey(a), compatSideKey(b), kind, part, ...(isMonthPart(part) && month ? [month] : []), locale].join('|') + (youth ? '|u20' : '')
}

/**
 * 공유받은 궁합 글 찾기 — 공유에는 상대 성별과 시기 조각의 달이 없어 그 자리를 비운 꼴
 * (PostgREST like: `_` 한 글자 · `*` 아무 글자). 앞사람(산 사람)의 성별은 결과에 저장된 값이다.
 */
export function compatKeyPattern(a: CompatKeySide, b: CompatKeySide, locale: Locale, kind: string, part: string, youth = false): string {
  return ['compat', COMPAT_VERSION, compatSideKey(a), `${sideOf(b)}._${tieOf(b)}`, kind, part, ...(isMonthPart(part) ? ['*'] : []), locale].join('|') + (youth ? '|u20' : '')
}

/**
 * **궁합 한 줄 설명**. 예전에는 점수 구간만 보고 골라, 둘이 같은 기운(丁×丁·火×火)이고 꼬리표가
 * "비슷함" 인데 한 줄은 "다른 점이 서로 균형을 맞춰 주는" 사이라고 했다. 구간 글 가운데 "다른 점" 을 말하는 둘
 * (balanced·challenging)은 같은 기운이면 같은 기운 글로 바꾼다. 나머지(생하는·극하는·합·충)는 서로 다른 기운이라 구간 글이 맞다.
 */
export function compatLineKey(result: Pick<CompatResult, 'band' | 'factors'>): string {
  const same = result.factors.some((f) => f.factor === 'sameElement')
  if (same && (result.band === 'balanced' || result.band === 'challenging')) return `compatBand_same_${result.band}`
  return `compatBand_${result.band}`
}

/**
 * **셋이 보는 일 궁합의 캐시 열쇠**. 읽는 사람(A)은 앞, 나머지 둘은 정렬한 차례 — 누구를 먼저 골랐든 같은 글이다.
 * 글의 {B}·{C} 는 이 정렬 차례를 따른다 (`teamSides` 가 돌려주는 `swapped` 로 화면이 이름을 맞춘다).
 */
export function teamSides(b: CompatKeySide, c: CompatKeySide): { first: CompatKeySide; second: CompatKeySide; swapped: boolean } {
  const swapped = compatSideKey(c) < compatSideKey(b)
  return swapped ? { first: c, second: b, swapped } : { first: b, second: c, swapped }
}

export function compatTeamCacheKey(a: CompatKeySide, b: CompatKeySide, c: CompatKeySide, locale: Locale, youth = false): string {
  const { first, second } = teamSides(b, c)
  return ['compat', 'team3', compatSideKey(a), compatSideKey(first), compatSideKey(second), locale].join('|') + (youth ? '|u20' : '')
}
